import React from "react";
import styled, { keyframes } from "styled-components";
import Helmet from "react-helmet";
import { H3, H4, P, Description } from "../components/typography";
import Emphasis from "../components/atoms/Emphasis";
import NavBar from "../components/NavBar";
import { InternalButton } from "../components/atoms/Button";
import Spacer from "../components/blocks/Spacer";
import Section from "../components/blocks/Section";
import FadeIn from "../components/atoms/FadeIn";
import { Editorial } from "../components/PublicationStyles";
import { getGraphNode } from "../selectors";
import favicon from "../../assets/logofavcon@2x.png";
import matthewImg from "../../matthew-nest.png";
import foamHandImg from "../../assets/foam-hand.png";

// -- Minimal custom styles (hero + stats only) --

const HeroBg = styled.div`
  background-color: ${props => props.theme.colors.dark};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 6rem 4rem 6rem;
  min-height: 70vh;

  ${props => props.theme.media.tablet`
    flex-direction: row;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 6rem 2rem 4rem;
    min-height: 0;
    gap: 1.5rem;
  `};

  ${props => props.theme.media.mobile`
    flex-direction: column;
    padding: 6rem 1rem 0;
    gap: 1rem;
  `};
`;

const HeroText = styled.div`
  text-align: left;
  max-width: 520px;

  ${props => props.theme.media.tablet`
    text-align: center;
  `};
`;

const HideMobileWrap = styled.div`
  width: 37vh;
  height: 55vh;
  flex-shrink: 0;
  ${props => props.theme.media.tablet`display: none;`};
`;

const CanisterInline = styled.div`
  display: none;
  flex-shrink: 0;

  ${props => props.theme.media.tablet`
    display: block;
    width: 180px;
    height: 260px;
  `};

  ${props => props.theme.media.mobile`
    width: 200px;
    height: 280px;
    margin: 0 auto;
  `};
`;

const HeroHeadline = styled.h2`
  font-family: "Palatino Linotype", "Book Antiqua", Palatino, serif;
  font-size: 3rem;
  font-weight: bold;
  color: ${props => props.theme.colors.white};
  line-height: 1.15;
  max-width: 520px;
  margin: 0;

  ${props => props.theme.media.tablet`
    font-size: 2.5rem;
  `};

  ${props => props.theme.media.mobile`
    font-size: 2rem;
  `};
`;

const HeroSub = styled.p`
  font-family: "Avenir Next", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 1.35rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.7;
  max-width: 420px;
  margin: 1.25rem 0 0;

  ${props => props.theme.media.tablet`
    margin: 1.25rem auto 0;
  `};

  ${props => props.theme.media.mobile`
    font-size: 1.1rem;
  `};
`;

const TealLine = styled.div`
  width: 48px;
  height: 2px;
  background: ${props => props.theme.colors.teal};
  margin: 2rem 0 0;

  ${props => props.theme.media.tablet`
    margin: 2rem auto 0;
  `};
`;

const HowItWorksWrap = styled.div`
  ${props => props.theme.media.tablet`
    padding-right: 0;
  `};
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  padding: 2rem 0;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;

  ${props => props.theme.media.mobile`
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  `};
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.teal};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.25rem;
`;

const StepContent = styled.div`flex: 1;`;

const TestimonialQuote = styled.blockquote`
  font-family: "Palatino Linotype", "Book Antiqua", Palatino, serif;
  font-size: 1.5rem;
  line-height: 1.6;
  font-style: italic;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  color: ${props => props.theme.colors.dark};

  ${props => props.theme.media.mobile`
    font-size: 1.25rem;
  `};
`;

const TestimonialAvatar = styled.img`
  width: 320px;
  height: 320px;
  border-radius: 8px;
  object-fit: cover;
`;

const TestimonialAttribution = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  justify-content: center;
`;

// -- Announcement pill variants --

const defaultPillText = "Contraline has acquired an exclusive license to NES/T and is advancing towards a global Phase 3 trial.";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(67, 188, 186, 0); }
  50% { box-shadow: 0 0 20px 4px rgba(67, 188, 186, 0.15); }
`;

const shimmerPill = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

const borderDraw = keyframes`
  0% { clip-path: inset(0 100% 0 0); }
  100% { clip-path: inset(0 0 0 0); }
`;

const PillBase = styled.div`
  border-radius: 2rem;
  padding: 0.75rem 2rem;
  max-width: 520px;
  text-align: center;
  font-family: "Avenir Next", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  animation: ${fadeIn} 0.6s ease-out both;
`;

// A: Subtle teal glow pulse
const PillA = PillBase.extend`
  background: #f7fafa;
  color: #000914;
  border: 1px solid #43bcba;
  animation: ${fadeIn} 0.6s ease-out both, ${pulseGlow} 3s ease-in-out 1s infinite;
`;

// B: Dark with shimmer sweep
const PillB = PillBase.extend`
  background: linear-gradient(
    105deg,
    #000914 0%, #000914 40%, #0f2828 48%, #163636 50%, #0f2828 52%, #000914 60%, #000914 100%
  );
  background-size: 300% 100%;
  color: white;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 0.5s both, ${shimmerPill} 10s ease-in-out 1.1s infinite;
`;

// C: Teal gradient with soft float
const floatUp = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const PillC = PillBase.extend`
  background: linear-gradient(135deg, #43bcba, #3ecfcd);
  color: white;
  font-weight: 600;
  animation: ${fadeIn} 0.6s ease-out both, ${floatUp} 4s ease-in-out 1s infinite;
`;

// D: Border draws in, then fades content
const PillD = PillBase.extend`
  background: transparent;
  color: #000914;
  position: relative;
  border: 2px solid #43bcba;
  animation: ${fadeIn} 0.8s ease-out both, ${borderDraw} 1s ease-out both;
`;

// E: Light with animated teal underline
const underlineGrow = keyframes`
  from { width: 0; }
  to { width: 60%; }
`;

const PillEWrap = styled.div`
  max-width: 520px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out both;
`;

const PillELine = styled.div`
  height: 2px;
  background: linear-gradient(90deg, transparent, #43bcba, transparent);
  margin: 0.5rem auto 0;
  animation: ${underlineGrow} 1.2s ease-out 0.6s both;
`;

const PublicationsHeader = styled.div`
  ${props => props.theme.media.mobile`
    padding-top: 32px;
  `};
`;

const Chevron = styled.div`
  text-align: center;
  margin-top: 8rem;
  opacity: 0;
  animation: ${fadeIn} 0.6s ease-out 1s both;
  color: #c3c3c3;
  font-size: 1.5rem;
`;

const AnnouncementVariants = ({ text }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    <PillB>{text}</PillB>
  </div>
);

// -- Page --

class NestoronePage extends React.Component {
  render() {
    const data = this.props.data;
    const content = data ? getGraphNode(data, "NesteronePage") : null;

    const heroHeadline = (content && content.heroHeadline) || "Introducing NES/T";
    const heroSubtitle = (content && content.heroSubtitle && content.heroSubtitle.heroSubtitle) || "A daily topical gel for male contraception. Fully reversible & effective in weeks.";
    const metaDescription = (content && content.metaDescription && content.metaDescription.metaDescription) || "NES/T Gel - A daily topical contraceptive for men. Reversible, effective, and easy to use.";
    const pillTextValue = (content && content.pillText && content.pillText.pillText) || defaultPillText;
    const howItWorksTitle = (content && content.howItWorksTitle) || "How NES/T Works";
    const step1Title = (content && content.step1Title) || "Apply once a day";
    const step1Body = (content && content.step1Body && content.step1Body.step1Body) || "On the shoulders and upper arms \u2013 morning or night. The pump delivers the same amount every time and it dries fast, so it fits easily into your routine.";
    const step2Title = (content && content.step2Title) || "Two FDA-approved hormones, one purpose";
    const step2Body = (content && content.step2Body && content.step2Body.step2Body) || "NES/T combines two hormones that work together. One temporarily pauses sperm production. The other maintains normal testosterone levels to support energy, mood, and sex drive. Designed for no trade-offs.";
    const step3Title = (content && content.step3Title) || "Reliable and reversible";
    const step3Body = (content && content.step3Body && content.step3Body.step3Body) || "Most men reach contraceptive levels in about two months. When you stop using the gel, sperm production gradually returns to normal.";
    const statusHeadline = (content && content.statusHeadline && content.statusHeadline.statusHeadline) || "NES/T Gel completed Phase 2b trials in 2024 with 462 couples, 0 pregnancies.";
    const statusSubheadline = (content && content.statusSubheadline) || "Phase three trials are anticipated to begin in 2026.";
    const testimonialQuote = (content && content.testimonialQuote && content.testimonialQuote.testimonialQuote) || "I've only experienced increased libido. Maybe I'm just lucking out but I hope this is the case for the majority of participants. If it is, it's definitely going to change contraception as a whole.";
    const testimonialName = (content && content.testimonialName) || "Matthew Trevi\u00f1o";
    const testimonialTitleText = (content && content.testimonialTitle) || "Phase 2b Trial Participant";
    const testimonialImageSrc = (content && content.testimonialImage && content.testimonialImage.responsiveResolution) ? content.testimonialImage.responsiveResolution.src : matthewImg;
    const publicationsTitle = (content && content.publicationsTitle) || "Publications";
    const publicationsDescription = (content && content.publicationsDescription && content.publicationsDescription.publicationsDescription) || "Selected research on NES/T Gel and combined hormonal male contraception.";
    const publications = content && content.publications;
    const ctaHeadline = (content && content.ctaHeadline && content.ctaHeadline.ctaHeadline) || "Effective, reversible contraception \u2014 on your terms.";
    const ctaButtonText = (content && content.ctaButtonText) || "Interested in the NES/T Trial?";
    const ctaButtonLink = (content && content.ctaButtonLink) || "/participate";
    const heroImageSrc = (content && content.heroImage && content.heroImage.responsiveResolution) ? content.heroImage.responsiveResolution.src : foamHandImg;

    return (
      <div>
        <Helmet
          title="NES/T Gel - Contraline"
          meta={[
            {
              name: "description",
              content: metaDescription
            }
          ]}
        >
          <link rel="icon" type="image/png" href={favicon} />
        </Helmet>
        <NavBar color="white" background="dark" />

        {/* Hero Section */}
        <HeroBg>
          <HeroText>
            <FadeIn>
              <HeroHeadline>{heroHeadline}</HeroHeadline>
              <HeroSub>{heroSubtitle}</HeroSub>
              <TealLine />
            </FadeIn>
          </HeroText>
          <HideMobileWrap>
            <img src={heroImageSrc} alt="NES/T Gel" style={{ maxHeight: "55vh", objectFit: "contain" }} />
          </HideMobileWrap>
          <CanisterInline>
            <img src={heroImageSrc} alt="NES/T Gel" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </CanisterInline>
        </HeroBg>

        {/* Mechanism summary + steps */}
        <Section color="white" narrow>
          <div>
          <Spacer span={4} />
          <HowItWorksWrap>
            <Emphasis>
              <H4>{howItWorksTitle}</H4>
            </Emphasis>
            <StepList>
              <Step>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <P>
                    <strong>{step1Title}</strong>
                  </P>
                  <P>{step1Body}</P>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <P>
                    <strong>{step2Title}</strong>
                  </P>
                  <P>{step2Body}</P>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <P>
                    <strong>{step3Title}</strong>
                  </P>
                  <P>{step3Body}</P>
                </StepContent>
              </Step>
            </StepList>
          </HowItWorksWrap>
          <Spacer span={4} />
          </div>
        </Section>

      {/* Program Status */}
      <Section color="greyLight" align="center">
        <Spacer span={6} />
        <H3 serif weight="bold" align="center" style={{ lineHeight: 1.5, marginBottom: "0.25rem" }}>
          {statusHeadline}
        </H3>
        {statusSubheadline && <H3 serif weight="bold" align="center" style={{ lineHeight: 1.5, marginTop: 0 }}>
          {statusSubheadline}
        </H3>}
        <Spacer span={6} />
        <TestimonialQuote>
          "{testimonialQuote}"
        </TestimonialQuote>
        <TestimonialAttribution>
          <TestimonialAvatar src={testimonialImageSrc} alt={testimonialName} />
          <div style={{ textAlign: "center" }}>
            <P color="dark" align="center" style={{ paddingBottom: 0 }}>
              {testimonialName} & Emily Fletcher
            </P>
            <P
              color="greyDark"
              align="center"
              style={{ fontSize: "0.9rem", paddingBottom: 0 }}
            >
              {testimonialTitleText}
            </P>
          </div>
        </TestimonialAttribution>
        <Spacer span={6} />
      </Section>

      {/* Publications */}
      <Section color="white" narrow align="center">
        <PublicationsHeader>
          <H3 serif weight="bold" align="center">
            {publicationsTitle}
          </H3>
        </PublicationsHeader>
        <P
          align="center"
          color="greyDark"
          style={{ marginBottom: "2rem" }}
        >
          {publicationsDescription}
        </P>
        <Editorial publications={publications} />
        <Spacer span={4} />
      </Section>

      <Section color="white" narrow>
        <Spacer span={4} />
        <FadeIn>
          <H3 serif weight="bold" align="center">
            {ctaHeadline}
          </H3>
          <Spacer span={2} />
          <div style={{ textAlign: "center", width: "100%" }}>
            <InternalButton to={ctaButtonLink}>
              {ctaButtonText}
            </InternalButton>
          </div>
        </FadeIn>
        <Spacer span={8} />
      </Section>
    </div>
    );
  }
}

export default NestoronePage;

export const pageQuery = graphql`
  query NesteronePageQuery {
    allContentfulNesteronePage {
      edges {
        node {
          heroHeadline
          heroImage {
            responsiveResolution(width: 800, quality: 80) {
              src
            }
          }
          heroSubtitle {
            heroSubtitle
          }
          metaDescription {
            metaDescription
          }
          pillText {
            pillText
          }
          howItWorksTitle
          step1Title
          step1Body {
            step1Body
          }
          step2Title
          step2Body {
            step2Body
          }
          step3Title
          step3Body {
            step3Body
          }
          statusHeadline {
            statusHeadline
          }
          statusSubheadline
          testimonialQuote {
            testimonialQuote
          }
          testimonialName
          testimonialTitle
          publicationsTitle
          publicationsDescription {
            publicationsDescription
          }
          publications {
            authors
            title
            journal
            year
            detail
            doi
          }
          ctaHeadline {
            ctaHeadline
          }
          ctaButtonText
          ctaButtonLink
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/nestorone.js